import type { AccessRules, Rule } from "./definitions/AccessRules";
import type { ConfigObj } from "./definitions/Configuration";

export function createPermissions<TRole extends string, TPermission extends string>(
  rules: AccessRules<TRole, TPermission>,
  configurations: Record<TRole, ConfigObj>
) {
  let _roles: Record<string, TRole[]> = {};
  let _rolesUpdated: Array<() => void> = [];
  let _rolesInitialized: Record<string, boolean> = {};

  const setUserRoles = (userId: string, roles: TRole[]) => {
    _roles[userId] = roles;
    _rolesInitialized[userId] = true;
    _rolesUpdated.forEach((callback) => callback());
  };

  const addRolesUpdatedListener = (callback: () => void) => {
    _rolesUpdated.push(callback);
  };

  const removeRolesUpdatedListener = (callback: () => void) => {
    _rolesUpdated = _rolesUpdated.filter((cb) => cb !== callback);
  };

  const ensureRolesInitialized = (userId: string, context: string) => {
    if (!_rolesInitialized[userId]) {
      throw new Error(`[Permissions ${context}] User roles have not been set yet for user ${userId}! Please call setUserRoles() prior to calling this method.`);
    }
  };

  const userHasRole = (userId: string, role: TRole) => {
    ensureRolesInitialized(userId, "userHasRole");
    return _roles[userId]?.includes(role);
  };

  const userHasPermissions = (userId: string, requiredPermissions: TPermission[] | TPermission) => {
    ensureRolesInitialized(userId, "userHasPermissions");
    return rolesHavePermissions(rules, _roles[userId], requiredPermissions);
  };

  const userHasConfigWithValue = <TKey extends keyof ConfigObj>(userId: string, config: TKey, value: ConfigObj[TKey]) => {
    ensureRolesInitialized(userId, "userHasValueForConfig");

    const roles = _roles[userId];
    if (!roles) return false;

    return roles.some((role) => configurations[role][config] === value);
  };

  const _resetPermissionsState_TEST_ONLY = () => {
    _roles = {};
    _rolesInitialized = {};
    _rolesUpdated = [];
  };

  return {
    setUserRoles,
    userHasRole,
    userHasPermissions,
    userHasConfigWithValue,
    addRolesUpdatedListener,
    removeRolesUpdatedListener,
    _resetPermissionsState_TEST_ONLY,
  };
}

const evaluateRule = (rule: Rule | undefined): boolean => {
  return !!rule;
};

const roleHasPermission = <TRole extends string, TPermission extends string>(rules: AccessRules<TRole, TPermission>, role: TRole, permission: TPermission) => {
  const roleRules = rules[role];
  if (!roleRules) {
    // no rules defined for role
    return false;
  }

  // rule for the provided permission for the provided role
  // true or () => boolean
  const rule = roleRules[permission];

  return evaluateRule(rule);
};

const rolesHavePermissions = <TRole extends string, TPermission extends string>(
  rules: AccessRules<TRole, TPermission>,
  roles: TRole[] = [],
  requiredPermissions: TPermission[] | TPermission
) => {
  requiredPermissions = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];

  // for *every* required permission
  return requiredPermissions.every((permission) =>
    // check that *some* role has that permission
    roles.some((role) => roleHasPermission(rules, role, permission))
  );
};
